import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowRight, Sparkles, CheckCircle2, 
  Calendar as CalendarIcon, ClipboardList, ShoppingCart, 
  Zap, Heart, Layout, Clock, Globe, Mail, Shield, Home,
  ShieldCheck, ChevronRight, Users, ShieldAlert, ZapOff
} from 'lucide-react';
import { Button } from '@/components/ui/Button';

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [activeSlide, setActiveSlide] = useState(0);

  // 🎡 Slideshow Controller
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const mockDate = new Date().toLocaleDateString(i18n.language, { 
    weekday: 'long', 
    month: 'short', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white selection:bg-indigo-100 transition-colors duration-300">
      
      {/* 1. Navigation */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto sticky top-0 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md z-50">
        <div className="flex items-center gap-2 font-black text-2xl tracking-tighter cursor-pointer" onClick={() => navigate('/')}>
          <div className="p-1.5 rounded-xl bg-indigo-600 text-white shadow-lg">
            <Home className="w-6 h-6" />
          </div>
          <span>Everyday<span className="text-indigo-600">OS</span></span>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="hidden md:flex items-center gap-1 bg-slate-100 dark:bg-slate-900/50 p-1 rounded-xl border border-slate-200 dark:border-slate-800">
            {['en', 'fr', 'es'].map((lng) => (
              <button 
                key={lng}
                onClick={() => i18n.changeLanguage(lng)} 
                className={`px-3 py-1 text-[10px] font-black rounded-lg transition-all ${
                  i18n.language.startsWith(lng) 
                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-white shadow-sm ring-1 ring-slate-200/10 dark:ring-slate-700' 
                    : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>

          <button 
            onClick={() => navigate('/pricing')} 
            className="text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors"
          >
            {t('nav.pricing', 'Pricing')}
          </button>
          
          <Button 
            onClick={() => navigate('/login')} 
            className="rounded-xl font-bold shadow-lg shadow-indigo-600/20 bg-indigo-600 text-white px-8 h-12"
          >
            {t('nav.login', 'Login')}
          </Button>
        </div>
      </nav>

      {/* 2. Hero Section */}
      <section className="px-8 pt-20 pb-16 max-w-6xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 text-xs font-black uppercase tracking-widest mb-8">
          <Sparkles className="w-4 h-4" /> {t('landing.hero_tag', 'The Standalone Household Protocol')}
        </div>
        <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.85] whitespace-pre-line">
          Simplify Your <br />
          <span className="text-indigo-600">Everyday</span>
        </h1>
        <p className="text-xl md:text-2xl text-slate-500 dark:text-slate-400 max-w-3xl mx-auto mb-10 font-medium leading-relaxed">
          {t('landing.hero_desc', 'EverydayOS is a central nervous system for your home. Manage schedules, complex maintenance, and inventory in a single, beautiful dashboard.')}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
          <Button onClick={() => navigate('/login')} size="lg" className="rounded-2xl px-10 h-16 text-lg font-black flex items-center gap-3 group bg-indigo-600 text-white">
            {t('landing.cta_primary', 'Get Started')} <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>

        {/* 3. Slideshow UI Preview */}
        <div className="relative max-w-5xl mx-auto group perspective-1000">
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-[3rem] opacity-20 blur-2xl transition-opacity" />
          
          <div className="relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden text-left min-h-[500px] flex flex-col">
            
            {/* Window Header */}
            <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-400/20 border border-red-400/50" />
                <div className="w-3 h-3 rounded-full bg-amber-400/20 border border-amber-400/50" />
                <div className="w-3 h-3 rounded-full bg-emerald-400/20 border border-emerald-400/50" />
              </div>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{mockDate} • Operational</div>
              <div className="flex gap-1">
                {[0, 1, 2].map(i => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${activeSlide === i ? 'bg-indigo-500 w-4' : 'bg-slate-200 dark:bg-slate-700'}`} />
                ))}
              </div>
            </div>

            {/* Slideshow Content */}
            <div className="flex-1 relative">
              {/* Slide 0: Calendar (Busy) */}
              <div className={`absolute inset-0 p-8 transition-all duration-700 flex flex-col gap-4 ${activeSlide === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500 mb-2">Household Agenda</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { t: '08:30', e: 'School Drop-off', c: 'border-blue-500' },
                    { t: '11:00', e: 'HVAC Filter Replacement', c: 'border-amber-500' },
                    { t: '13:30', e: 'Grocery Restock Trip', c: 'border-emerald-500' },
                    { t: '16:00', e: 'Vet Appointment (Luna)', c: 'border-rose-500' },
                    { t: '19:00', e: 'Community Board Meeting', c: 'border-indigo-500' }
                  ].map((item, i) => (
                    <div key={i} className={`p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border-l-4 ${item.c} shadow-sm`}>
                      <span className="text-[10px] font-black text-slate-400">{item.t}</span>
                      <p className="font-bold text-sm dark:text-white">{item.e}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Slide 1: Tasks (Different States) */}
              <div className={`absolute inset-0 p-8 transition-all duration-700 flex flex-col gap-4 ${activeSlide === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                <h3 className="text-xs font-black uppercase tracking-widest text-emerald-500 mb-2">Task Pulse</h3>
                <div className="space-y-3 max-w-2xl">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100 dark:border-emerald-800 flex items-center justify-between">
                    <span className="font-bold text-sm text-emerald-700 line-through">Mow Front Lawn</span>
                    <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between group shadow-sm">
                    <span className="font-bold text-sm">Clean Gutters (North Side)</span>
                    <div className="px-2 py-1 rounded bg-amber-100 text-amber-600 text-[8px] font-black uppercase">In Progress</div>
                  </div>
                  <div className="p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 flex items-center justify-between shadow-sm">
                    <span className="font-bold text-sm">Organize Pantry Shelves</span>
                    <div className="px-2 py-1 rounded bg-slate-100 text-slate-400 text-[8px] font-black uppercase">Todo</div>
                  </div>
                  <div className="p-4 bg-rose-50 dark:bg-rose-900/10 rounded-2xl border border-rose-100 dark:border-rose-900/20 flex items-center justify-between">
                    <span className="font-bold text-sm text-rose-600">Renew Home Insurance</span>
                    <div className="px-2 py-1 rounded bg-rose-600 text-white text-[8px] font-black uppercase">Overdue</div>
                  </div>
                </div>
              </div>

              {/* Slide 2: Groceries (Quantities & Values) */}
              <div className={`absolute inset-0 p-8 transition-all duration-700 flex flex-col gap-4 ${activeSlide === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                <h3 className="text-xs font-black uppercase tracking-widest text-rose-500 mb-2">Automated Inventory</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { n: 'Whole Milk', q: '2 Gal', v: 'Low' },
                    { n: 'Avocados', q: '4 Units', v: 'Stocked' },
                    { n: 'Greek Yogurt', q: '2 Tubs', v: 'Low' },
                    { n: 'Chicken Breast', q: '1.5kg', v: 'Out' },
                    { n: 'Eggs', q: '12 Pack', v: 'Stocked' },
                    { n: 'Sourdough', q: '1 Loaf', v: 'Out' },
                    { n: 'Bananas', q: '1 Bunch', v: 'Low' },
                    { n: 'Coffee Beans', q: '500g', v: 'Stocked' }
                  ].map((item, i) => (
                    <div key={i} className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700 flex flex-col gap-1 shadow-sm">
                      <div className="flex justify-between items-start">
                        <span className="font-bold text-xs">{item.n}</span>
                        <div className={`w-1.5 h-1.5 rounded-full ${item.v === 'Stocked' ? 'bg-emerald-500' : item.v === 'Low' ? 'bg-amber-500' : 'bg-rose-500'}`} />
                      </div>
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{item.q} • {item.v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Core Features Section (Expanded to 4) */}
      <section className="px-8 py-24 bg-slate-50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <h2 className="text-4xl font-black mb-4">A Professional OS for your Private Life</h2>
            <p className="text-lg text-slate-500 font-medium max-w-2xl mx-auto">Traditional apps track users. We track households. Built to solve the unique friction of shared living.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:border-indigo-500/50 transition-all group">
              <Users className="w-8 h-8 text-indigo-600 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-black mb-2">Multi-Tenancy</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Switch instantly between your home, rental properties, or parents' house without logging out.</p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:border-emerald-500/50 transition-all group">
              <ZapOff className="w-8 h-8 text-emerald-600 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-black mb-2">Zero Tracking</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Unlike "Free" family apps, we don't sell your grocery data to advertisers. Your home is a private server.</p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:border-amber-500/50 transition-all group">
              <ShieldAlert className="w-8 h-8 text-amber-600 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-black mb-2">Temporal Maintenance</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Auto-calculates the life-cycle of your home infrastructure. Know when to service items before they break.</p>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm hover:border-rose-500/50 transition-all group">
              <Layout className="w-8 h-8 text-rose-600 mb-6 group-hover:scale-110 transition-transform" />
              <h3 className="text-lg font-black mb-2">Modular Grid</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Every house is different. Customize your dashboard layout to prioritize what your specific family needs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CTA Section (Reimplemented) */}
      <section className="px-8 py-20">
        <div className="max-w-4xl mx-auto bg-indigo-600 rounded-[3.5rem] p-12 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="relative z-10">
            <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">Ready to sync your home?</h2>
            <p className="text-indigo-100 font-bold mb-10 text-lg opacity-80">Make the move and take back control of your time.</p>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-white text-indigo-600 hover:bg-indigo-50 rounded-2xl px-12 h-16 font-black text-xl shadow-xl"
            >
              Start for Free
            </Button>
          </div>
        </div>
      </section>

      {/* 6. Comprehensive Footer */}
      <footer className="bg-slate-50 dark:bg-slate-900 pt-20 pb-10 px-8 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            
            {/* Brand Column */}
            <div className="col-span-1 md:col-span-1">
              <div className="flex items-center gap-2 font-black text-xl tracking-tighter mb-4">
                <Home className="w-5 h-5 text-indigo-600" />
                <span>Everyday<span className="text-indigo-600">OS</span></span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-medium leading-relaxed mb-4">
                The standalone operating system for modern households. We prioritize privacy, data ownership, and clean logistics.
              </p>
              {/* Google Data Usage Mention */}
              <div className="flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                  Verified Data Usage: Only basic profile & email are utilized for auth.
                </span>
              </div>
            </div>

            {/* Pages Column */}
            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Pages</h4>
              <ul className="space-y-4">
                <li><Link to="/pricing" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Pricing</Link></li>
                <li><Link to="/login" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Dashboard</Link></li>
                <li><Link to="/inventory" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Groceries</Link></li>
              </ul>
            </div>

            {/* Legal Column */}
            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Legal</h4>
              <ul className="space-y-4">
                <li><Link to="/privacy" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-sm font-bold text-slate-600 dark:text-slate-300 hover:text-indigo-600 transition-colors">Terms of Service</Link></li>
              </ul>
            </div>

            {/* Support Column */}
            <div>
              <h4 className="font-black uppercase text-xs tracking-widest text-slate-400 mb-6">Support</h4>
              <ul className="space-y-4">
                <li className="flex items-center gap-2 text-sm font-bold text-slate-600 dark:text-slate-300">
                  <Mail className="w-4 h-4 text-indigo-600" /> nathanielaniballi@gmail.com
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
            <p>© {new Date().getFullYear()} EverydayOS Management • Built by households for households.</p>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Security Protocol 12.0 Active</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};